import { stationsKeys } from "@/api/station-query";
import { useMutation, useQueryCache } from "@pinia/colada";
import { Site, Station, useApi } from "@soft-db/monitoring-api-client";
import { sitesKeys } from "../api/site-query";
import { useSnackbarStore } from "../stores";
import { useTableSelections } from "./table-selections";

type WithFavorite = { id: string; inFavorite: boolean };
type FavoriteOptions<T extends WithFavorite> = {
	data: Ref<T[]>;
	add: (ids: string[]) => Promise<void>;
	remove: (ids: string[]) => Promise<void>;
	favoriteKey: readonly string[];
	contextKey: readonly string[];
};
export function useFavoriteSelection<T extends WithFavorite>(options: FavoriteOptions<T>) {
	const cache = useQueryCache();

	const snackbarStore = useSnackbarStore();

	const addMutation = useMutation({
		mutation: options.add,
		onMutate: (ids) => {
			cache.setQueryData<T[]>(options.contextKey, (previous) => {
				if (previous == null) {
					return [];
				}

				const idsSet = new Set(ids);
				for (const data of previous) {
					if (idsSet.has(data.id)) {
						data.inFavorite = true;
					}
				}

				return [...previous];
			});
		},
		onSuccess: () => {
			cache.invalidateQueries({ key: options.favoriteKey });
			snackbarStore.openOnSaved();
		},
	});

	const removeMutation = useMutation({
		mutation: options.remove,
		onMutate: (ids) => {
			cache.setQueryData<T[]>(options.contextKey, (previous) => {
				if (previous == null) {
					return [];
				}

				const idsSet = new Set(ids);
				for (const data of previous) {
					if (idsSet.has(data.id)) {
						data.inFavorite = false;
					}
				}

				return [...previous];
			});
		},
		onSuccess: () => {
			cache.invalidateQueries({ key: options.favoriteKey });
			snackbarStore.openOnSaved();
		},
	});

	const favorites = ref(new Set<string>());
	watchImmediate(options.data, () => {
		favorites.value = new Set(options.data.value.filter((d) => d.inFavorite).map((d) => d.id));
	});

	const { selections } = useTableSelections(favorites, {
		onChange: ({ items, value }) => {
			if (value) {
				addMutation.mutate(items);
			} else {
				removeMutation.mutate(items);
			}
		},
	});

	return selections;
}

export function useFavoriteStationSelection(stations: Ref<Station[]>) {
	const api = useApi().currentUser();
	return useFavoriteSelection({
		data: stations,
		add: api.addMultipleStations.bind(api),
		remove: api.removeMultipleStations.bind(api),
		contextKey: stationsKeys.byContext(),
		favoriteKey: stationsKeys.byFavorites(),
	});
}

export function useFavoriteSiteSelection(sites: Ref<Site[]>) {
	const api = useApi().currentUser();
	return useFavoriteSelection({
		data: sites,
		add: api.addMultipleSites.bind(api),
		remove: api.removeMultipleSites.bind(api),
		contextKey: sitesKeys.byContext(),
		favoriteKey: sitesKeys.byFavorites(),
	});
}
